/**
 * Question Types and Decision Methods
 * Based on game_model.yaml questions section
 */

import { PlayerRole } from './Player';
import { PlayerScores } from './Player';
import { ItemId } from './Inventory';

export type QuestionId =
  | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Q5' | 'Q6' | 'Q7' | 'Q8' | 'Q9'
  | 'Q10A' | 'Q10B' | 'Q11B'
  | 'Q12A' | 'Q12B'
  | 'Q13A' | 'Q13B'
  | 'Q14A' | 'Q14B';

export type QuestionType =
  | 'text_input'
  | 'multiple_choice'
  | 'number_input'
  | 'item_selection';

export type DecisionMethod =
  | 'individual'            // Each player decides independently
  | 'majority_vote'         // Most common answer wins
  | 'majority_vote_exclude_neutral' // Exclude neutral option from count
  | 'unanimous_A'           // All must select A to proceed
  | 'consensus';            // All must enter same value

export type AnswerValue = string | number | ItemId;

export interface QuestionOption {
  id: string;           // A, B, C, D
  label: string;
  description?: string; // Optional description shown below the label
  value: number;
}

export interface Question {
  id: QuestionId;
  type: QuestionType;
  question: string;
  stage: number;
  step?: string;
  variant?: 'A' | 'B';  // For Stage 5 variants

  // Options for multiple choice
  options?: QuestionOption[];

  // For number input
  min?: number;
  max?: number | string; // Can be "team.energy_packs"

  // For item selection
  optionsFrom?: string; // "team.inventory"
  maxSelections?: number;

  // Decision handling
  individual?: boolean;
  requiresConsensus?: boolean;
  consensusMethod?: string;
  decisionMethod?: DecisionMethod;
  neutralOption?: string;
  isolated?: boolean;   // Player alone, no visibility
  onlyFor?: string;     // Conditional (e.g., "players where Q11B == 'B'")

  // Scoring
  scoring?: Record<string, PlayerScores> | { default: Record<string, PlayerScores>; FIXER?: Record<string, PlayerScores> };

  // Effects
  effects?: QuestionEffect[];
}

export interface QuestionEffect {
  condition?: string;
  action: string;
  target?: PlayerRole | string;
  value?: any;
  amount?: number | string;
  description?: string;
  flag?: string;
  items?: string;
}

export interface PlayerDecision {
  playerId: string;
  questionId: QuestionId;
  answer: AnswerValue;
  timestamp: Date;
}

export interface TeamDecision {
  questionId: QuestionId;
  playerDecisions: PlayerDecision[];
  finalAnswer?: AnswerValue;
  decisionMethod: DecisionMethod;
  timestamp: Date;
}

/**
 * Question definitions from game_model.yaml
 */
export const QUESTIONS: Record<QuestionId, Question> = {
  Q1: {
    id: 'Q1',
    type: 'text_input',
    question: 'What should your teammates call you?',
    stage: 0,
    step: '0.1',
    individual: true
  },

  Q2: {
    id: 'Q2',
    type: 'text_input',
    question: 'What is your understanding of your team\'s mission thus far?',
    stage: 0,
    step: '0.1',
    individual: true
  },

  Q3: {
    id: 'Q3',
    type: 'text_input',
    question: 'What would you like your team\'s name to be?',
    stage: 0,
    step: '0.2',
    requiresConsensus: true,
    consensusMethod: 'most_common'
  },

  Q4: {
    id: 'Q4',
    type: 'multiple_choice',
    question: 'As a team which tub do you want to take for the mission?',
    stage: 0,
    step: '0.3',
    options: [
      { id: 'A', label: 'Tub A', description: 'Bio transport capsules, Environmental Cleanse, Extra Diving Bell parts, Extra nets and fins, Two energy meals', value: 1 },
      { id: 'B', label: 'Tub B', description: 'Bio transport capsules, Emergency decomp suit, Environmental Cleanse, Extra nets and fins, Translation device', value: 2 },
      { id: 'C', label: 'Tub C', description: 'Bio transport capsules, Emergency decomp suit, Four energy meals, Photon locator, Translation device', value: 3 },
      { id: 'D', label: 'None of these', value: 4 }
    ],
    decisionMethod: 'majority_vote',
    scoring: {
      A: { brain: 0, heart: 0, task: 1, communication: 1 },
      B: { brain: 0, heart: 0, task: 1, communication: 1 },
      C: { brain: 0, heart: 0, task: 1, communication: 1 },
      D: { brain: 0, heart: 0, task: 0, communication: 0 }
    }
  },

  Q5: {
    id: 'Q5',
    type: 'multiple_choice',
    question: 'When do you choose to teleport to the Ocean Platform A-51?',
    stage: 0,
    step: '0.4',
    options: [
      { id: 'A', label: 'Right now. Let\'s do it!', value: 1 },
      { id: 'B', label: 'Wait until first light', value: 2 }
    ],
    decisionMethod: 'majority_vote',
    scoring: {
      A: { brain: 1, heart: 0, task: 1, communication: 1 },
      B: { brain: 0, heart: 1, task: 0, communication: 0 }
    }
  },

  Q6: {
    id: 'Q6',
    type: 'multiple_choice',
    question: 'What does the team choose to do?',
    stage: 1,
    step: '1.1',
    options: [
      { id: 'A', label: 'Wait a day and send GEE back to the Mothership for another tub', value: 1 },
      { id: 'B', label: 'Whatever everyone else wants to do', value: 2 },
      { id: 'C', label: 'Move forward now with the items in the Diving Bell', value: 3 }
    ],
    decisionMethod: 'majority_vote_exclude_neutral',
    neutralOption: 'B',
    scoring: {
      A: { brain: 1, heart: 1, task: 1, communication: 1 },
      B: { brain: 0, heart: 0, task: 0, communication: 0 },
      C: { brain: 1, heart: 0, task: 1, communication: 1 }
    }
  },

  Q7: {
    id: 'Q7',
    type: 'multiple_choice',
    question: 'How do you choose to spend the day?',
    stage: 2,
    step: '2.1',
    individual: true,
    options: [
      { id: 'A', label: 'Cave', value: 1 },
      { id: 'B', label: 'Ship', value: 2 },
      { id: 'C', label: 'Both (Cave and Ship)', value: 3 }
    ],
    scoring: {
      default: {
        A: { brain: 2, heart: 1, task: 2, communication: 1 },
        B: { brain: 1, heart: 0, task: 1, communication: 0 },
        C: { brain: 1, heart: 1, task: 1, communication: 1 }
      },
      FIXER: {
        A: { brain: 2, heart: 1, task: 2, communication: 1 },
        B: { brain: -2, heart: -2, task: -2, communication: -2 },
        C: { brain: 0, heart: 0, task: 0, communication: 0 }
      }
    }
  },

  Q8: {
    id: 'Q8',
    type: 'multiple_choice',
    question: 'Is the team ready to call it a night?',
    stage: 3,
    step: '3.1',
    options: [
      { id: 'A', label: 'Yes. Let\'s go to sleep. Tomorrow\'s a big day.', value: 1 },
      { id: 'B', label: 'No. Let\'s stay up. There\'s more to discuss.', value: 2 }
    ],
    decisionMethod: 'unanimous_A'
  },

  Q9: {
    id: 'Q9',
    type: 'multiple_choice',
    question: 'What do you choose to do?',
    stage: 4,
    step: '4.1',
    individual: true,
    options: [
      { id: 'A', label: 'Stay on the Articus II to conserve energy', value: 1 },
      { id: 'B', label: 'Capture a Rainbow Ray', value: 2 },
      { id: 'C', label: 'Clean up the pollution', value: 3 },
      { id: 'D', label: 'Capture a Golden Octopus', value: 4 }
    ],
    scoring: {
      A: { brain: 0, heart: 0, task: 0, communication: 0 },
      B: { brain: 1, heart: 0, task: 2, communication: 1 },
      C: { brain: 1, heart: 2, task: 1, communication: 1 },
      D: { brain: 1, heart: 0, task: 1, communication: 0 }
    }
  },

  Q10A: {
    id: 'Q10A',
    type: 'multiple_choice',
    question: 'Do you have a plan?',
    stage: 5,
    variant: 'A',
    options: [
      { id: 'A', label: 'Yes, let\'s continue.', value: 1 },
      { id: 'B', label: 'No, but let\'s continue anyway.', value: 2 }
    ]
  },

  Q10B: {
    id: 'Q10B',
    type: 'multiple_choice',
    question: 'Do you have a plan?',
    stage: 5,
    variant: 'B',
    options: [
      { id: 'A', label: 'Yes, let\'s continue.', value: 1 },
      { id: 'B', label: 'No, but let\'s continue anyway.', value: 2 }
    ]
  },

  Q11B: {
    id: 'Q11B',
    type: 'multiple_choice',
    question: 'What will you do?',
    stage: 5,
    variant: 'B',
    individual: true,
    options: [
      { id: 'A', label: 'I\'m staying on the Diving Bell.', value: 1 },
      { id: 'B', label: 'I\'ll swim for it.', value: 2 }
    ]
  },

  Q12A: {
    id: 'Q12A',
    type: 'number_input',
    question: 'You are alone in the escape chamber. You can choose how many energies to consume. No one can stop you from your choice. How many do you choose?',
    stage: 5,
    variant: 'A',
    individual: true,
    isolated: true,
    min: 0,
    max: 'team.energy_packs'
  },

  Q12B: {
    id: 'Q12B',
    type: 'number_input',
    question: 'You are alone in the escape chamber. You can choose how many energies to consume. No one can stop you from your choice. How many do you choose?',
    stage: 5,
    variant: 'B',
    individual: true,
    isolated: true,
    onlyFor: 'players where Q11B == "B"',
    min: 0,
    max: 'team.energy_packs'
  },

  Q13A: {
    id: 'Q13A',
    type: 'item_selection',
    question: 'Now, what do you want to take with you when you go?',
    stage: 5,
    variant: 'A',
    individual: true,
    optionsFrom: 'team.inventory',
    maxSelections: 1
  },

  Q13B: {
    id: 'Q13B',
    type: 'item_selection',
    question: 'Now, what do you want to take with you when you go?',
    stage: 5,
    variant: 'B',
    individual: true,
    onlyFor: 'players where Q11B == "B"',
    optionsFrom: 'team.inventory',
    maxSelections: 1
  },

  Q14A: {
    id: 'Q14A',
    type: 'multiple_choice',
    question: 'Pressure is mounting, you must ascend from the bottom, what do you want to do?',
    stage: 5,
    variant: 'A',
    individual: true,
    options: [
      { id: 'A', label: 'Rest in the cave for one day before ascending', value: 1 },
      { id: 'B', label: 'Ascend slowly and safely in the Diving Bell', value: 2 },
      { id: 'C', label: 'Swim for the surface (dangerous but fast)', value: 3 }
    ]
  },

  Q14B: {
    id: 'Q14B',
    type: 'multiple_choice',
    question: 'Pressure is mounting, you must ascend from the bottom, what do you want to do?',
    stage: 5,
    variant: 'B',
    individual: true,
    onlyFor: 'players where Q11B == "B"',
    options: [
      { id: 'A', label: 'Rest in the cave for one day before ascending', value: 1 },
      { id: 'B', label: 'Ascend slowly and safely', value: 2 },
      { id: 'C', label: 'Swim for the surface (dangerous but fast)', value: 3 }
    ]
  }
};
